// openModal/closeModal/closeAllModals/confirmDialog/alertDialog/promptDialog
// (@symbo.ls/windows) and openTooltip/closeTooltip (@symbo.ls/tooltip) are
// NOT re-exported here —
// the smbls runtime supplies them (prepareContext, smbls cedec5903); a bag
// re-export of an externalized specifier publishes as ""
// (WORKSPACE-SHELL-UNDECLARED-IMPORTS-1). The dropdown pair is the
// exception: `openDropdown` (the house defaults) and `closeDropdown` are
// real wrappers below, so every library that merges shared reaches ONE
// closeDropdown — the shell's bare plugin re-export published as "" and
// preview's same-named navbar reset collided with it.
// MODAL-X-OVERFLOW-1 — safe-default-maxHeight wrapper for
// `openModal({ template: 'custom' })` layers. See openCustomModal.js.
export * from './openCustomModal.js'

// @symbo.ls/dropdown — `el.call('openDropdown' | 'closeDropdown', opts)`.
// The matching DropdownRoot component is wired via shared/components.
// Apps mount `DropdownRoot: {}` once in their root tree (see
// workspace/app.js next to ModalRoot).
import * as _dropdownPlugin from '@symbo.ls/dropdown'

// PASSTHROUGH — no app-wide theme default. The 2026-06-09 glass default
// ("all dropdowns should have theme canvas-card-glass") is superseded by
// FW-DROPDOWN-PANEL-STALE-SHELL-PAINT-1: the dropdown Panel is a
// chromeless positioning shell, bodies own their card paint, and shell
// chrome (`theme`/`round`/`shadow`) is per-call OPT-IN. A shell default
// forced every self-painting body to COVER it — width/radius mismatches
// showed as stale stripes/corner peeks, and every dropdown stacked two
// shadows. The shell-reliant callers (notifications, canvasBgPicker,
// messengerTabs, meetLayout, aiModel, arpPicker, aiParams) now opt in at
// their call sites. The wrapper function itself stays: it is the single
// explicit re-export every `el.call('openDropdown')` resolves through
// (bag re-exports of externalized specifiers publish as "" —
// WORKSPACE-SHELL-UNDECLARED-IMPORTS-1) and the seam for any future
// app-wide default.
//
// FA204 / frank trap — reads `_dropdownPlugin.openDropdown` directly instead
// of through a derived module-scope const (`_openDropdown`): the import
// namespace itself resolves fine post-frank (imports fall back through
// `globalScope`), but a plain module-scope const derived FROM it does not —
// same class as every other file in this sweep (project_frank_drops_module_
// local_helpers). See functions/pointerCapability.js / mwtFlag.js for the
// sibling window/globalThis-state variant of this same fix.
//
// TWO defaults live here, both about the layer's ELEVATION (every other
// descriptor key passes through untouched; the caller's object is never
// mutated). Pinned by __tests__/themedDropdownLayerCarriesElevation.test.js.
//
// 1. The EDGE (2026-10-01, Nika: "fix dropdown backgrounds in apps. for
//    example call dropdown."): a layer that opts into a surface (`theme`) is
//    an elevated surface, so it carries the house elevation token
//    (`shadow: 'overlay'`, the token MenuPanel declares) unless the caller
//    names one — a token, or `null` to opt out. The body's own shadow never
//    shows in a dropdown: the plugin Panel scrolls (overflow auto) and clips
//    it, and an unshadowed Panel is forced to `box-shadow: none`. A FLAT
//    inline popover that passes a theme must pass `shadow: null`.
//
// 2. The GROUND (design-d2, 2026-10-01: a floating layer sits ONE STEP above
//    its ground): over the page a menu keeps the house `dialog` ground; when
//    the ANCHOR sits inside a dialog — a DOMQL ancestor with
//    `role: 'dialog'`, the marker AppDialog's panel carries — the house
//    ground would be the dialog's own fill (1.00:1), so it takes the
//    `raised` token (white / gray5: dark 1.62:1 plus the ring, light
//    unchanged). Only the HOUSE ground moves: a layer themed `dialog` gets
//    `background: 'raised'` (the plugin paints it over the theme), and the
//    body — which paints its own ground over the layer (MenuPanel: `dialog`)
//    — gets `background: 'raised'` when its own theme, or the theme of the
//    component it extends, is `dialog` or none. Any other explicit theme, an
//    explicit `background`, and an unthemed layer's chrome are kept.
//
// All of it lives inside the function (Rule 33 — no module-level helpers).
export function openDropdown(opts) {
  if (!opts || typeof opts !== 'object') return _dropdownPlugin.openDropdown.call(this, opts)
  const o = { ...opts }
  if (o.theme && o.shadow === undefined) o.shadow = 'overlay'
  let overDialog = false
  const anchor =
    o.anchorEl && typeof o.anchorEl === 'object' && 'parent' in o.anchorEl ? o.anchorEl : null
  for (let p = anchor, depth = 0; p && depth < 256; p = p.parent, depth++) {
    if (p.role === 'dialog') {
      overDialog = true
      break
    }
  }
  if (overDialog && (!o.theme || o.theme === 'dialog')) {
    if (o.theme && o.background === undefined) o.background = 'raised'
    const c = o.content
    if (c && typeof c === 'object' && c.background === undefined) {
      const components = (this && this.context && this.context.components) || {}
      const base = typeof c.extends === 'string' ? components[c.extends] : null
      const bodyTheme = c.theme !== undefined ? c.theme : base && base.theme
      if (!bodyTheme || bodyTheme === 'dialog') o.content = { ...c, background: 'raised' }
    }
  }
  // 3. THE ROOT MIRROR (design-d2, 2026-10-01): the open layer's id on the
  // root (`_dropdownLayer`), set by the layer's own onOpen and cleared by its
  // onClose — the plugin fires both on every open, replace and close. A
  // surface below a menu reads it reactively (the composer's focus panel,
  // _simone/Suggestions.js, stops painting under a menu). The caller's own
  // onOpen / onClose still run.
  const rootState =
    (this && this.state && this.state.root) ||
    (this && typeof this.getRoot === 'function' && this.getRoot() && this.getRoot().state) ||
    null
  if (rootState && typeof rootState.update === 'function') {
    const ownOpen = o.onOpen
    const ownClose = o.onClose
    const mark = o.id || true
    o.onOpen = (layer) => {
      rootState.update({ _dropdownLayer: mark }, { preventFetch: true })
      if (typeof ownOpen === 'function') return ownOpen(layer)
    }
    o.onClose = (layer) => {
      if (rootState._dropdownLayer === mark)
        rootState.update({ _dropdownLayer: null }, { preventFetch: true })
      if (typeof ownClose === 'function') return ownClose(layer)
    }
  }
  return _dropdownPlugin.openDropdown.call(this, o)
}

// el.call('closeDropdown', id?) — the dropdown plugin's close, forwarded as is
// (this = the calling element, every argument, the return value). One owner:
// see the header note above.
export function closeDropdown(...args) {
  return _dropdownPlugin.closeDropdown.apply(this, args)
}

// setDropdownInert has been CALLED since the July /studio drag-through fix
// (smbls 834d6ba23 + workspace 1c483192) but was never re-exported here, so
// `el.call('setDropdownInert')` warned "not registered" and silently no-oped
// in every environment — the whole dismiss-inert mechanism never actually
// ran (found live 2026-08-06). The CI lock still pins
// @symbo.ls/dropdown@3.14.470, which PREDATES the export, and Parcel's
// scope-hoisting statically rejects both a named import AND a direct
// namespace member access of a missing export ("does not export
// 'setDropdownInert'" — run e25719b7's red deploy). COMPUTED access
// through an identifier is beyond that static check: undefined → no-op on
// the old pinned plugin, the real function locally and once the lock
// catches up to ≥3.14.650 (then simplify to a named import).
// FA204 / frank trap — no module-scope const for the key; AND parcel's
// static check constant-folds a string-LITERAL member access too (run
// 31546534995's red deploy: "does not export 'setDropdownInert'"), so the
// key is join-computed inside the function — beyond the folder, still
// frank-safe. Simplify to a named import once the lock is ≥3.14.650.
export function setDropdownInert(...args) {
  const key = ['set', 'Dropdown', 'Inert'].join('')
  const fn = _dropdownPlugin[key]
  if (typeof fn !== 'function') return
  return fn.apply(this, args)
}

export * from './fetchNpmPackage.js'
export * from './resolveScope.js'
export * from './reloadIntegrationHub.js'
export * from './slugify.js'
export * from './openProjectDomainFlow.js'
export * from './iconSlugDenylist.js'
export * from './profilePath.js'
export * from './cycleTheme.js'
export * from './sameOriginTop.js'
export * from './uploadFile.js'
export * from './getLastLocationPath.js'
export * from './agentDisplayName.js'
export * from './changeGlobalTheme.js'
// The assistant's session surface: shared owns each name, the hosts register
// prefixed implementations (see ./aiSessionHost.js).
export {
  sendPrompt,
  newSession,
  switchSession,
  deleteSession,
  hydrateServerSessions,
  restoreActiveThread,
  buildAiAwareness
} from './aiSessionHost.js'
export * from './applyDesignSystemSummaryCollection.js'
export * from './applyMarkdownToElement.js'
export * from './confirmEmail.js'
export * from './previewConfirmation.js'
export * from './completeConfirmSignIn.js'
export * from './searchNpmPackages.js'
export * from './refreshUserProjects.js'
export * from './getErroredCodeKey.js'
export * from './loadProjectData.js'
export * from './updateApplicationState.js'
export * from './getCodePreviewValue.js'
export * from './addSmblsSubdomain.js'
export * from './checkFeatureByPackage.js'
// Per-team app-access CRUD gate — el.call('canApp', appKey, verb).
// Consumed directly (not just via el.call) by featureFlags.js's
// navItemVisible, which is how it reaches the dock, dropdown, and
// launcher tile filters without each needing its own import.
export * from './appAccess.js'
export * from './a11yPrefs.js'
// MemberProfile permission gate — canAdmin / canSuperadmin / isSuperAdmin /
// canAccessPrivate / canEditProfile. See memberProfileAccess.js header for
// why this replicates (not imports) the workspace-side claim checks.
export * from './memberProfileAccess.js'
// AI-composer content references — trigger grammar + SDK option fetchers
// mirroring the chat composer's mention machinery under distinct names
// (Rule 63 — the chat context folds into the same registry). See
// mentionRefs.js header.
export * from './mentionRefs.js'
// THE keyboard model for every AI + chat dropdown (arrow cycle, Enter/Space
// activation, Escape close, focus-in-on-open / focus-back-on-close). Lifted
// out of ContextDropdown's scope so the AI menus, the composer '+' menu and
// chat's message menu share ONE implementation. See menuKeyboard.js header.
export * from './menuKeyboard.js'
// THE pre-send key model of the AI composer (attach-menu arrows + mention
// picker traversal), delegated to by BOTH mounts of CanvasPromptTextarea —
// canvas's own component and the shell's mount-site override in
// workspace/app.js, which replaces the component's onKeydown wholesale. See
// aiComposerKeys.js header for why the shell cannot inherit those branches.
export * from './aiComposerKeys.js'
// The composer's FOCUS PANEL — open it on a section, put it away, ask whether
// it is on screen (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1).
export * from './aiPanelFocus.js'
// THE accessor for `root.workspace.settings` — el.call('workspaceSettings', s)
// → { settings, ready } (tickets/spec-workspace-record-shape.md §5). Registered
// here (unlike its sibling activeWorkspaceRecord.js, which is import-by-path
// only) precisely BECAUSE components need it: a render site has to be able to
// ask "is this workspace actually loaded?" from inside a prop factory without
// an import, or it will keep reaching for `s.root.workspace.settings.X` and
// painting defaults over a workspace that simply hasn't arrived yet.
export * from './workspaceSettings.js'
// THE store-item copy resolver (record i18n ▸ `marketplace.app.<slug>.*`
// polyglot key ▸ the record's own copy). Registered HERE, in the shared
// barrel, because two surfaces read one app's name: the App Store, and the
// shell's navbar tab + launcher tile for that same installed app. It is EAGER
// on purpose — marketplace's own function barrel folds in only on a
// `/marketplace*` route, so a shell-route render site cannot reach it there.
// See the file header for the three sources and why there is never a fourth.
export * from './storeItemText.js'
export * from './sceneActions.js'
export * from './createBucket.js'
export * from './findModal.js'
export * from './arraizeIconsCategory.js'
export * from './prettifyCode.js'
export * from './loadIntercom.js'
export * from './codifyFetch.js'
export * from './getProjectMembers.js'
export * from './inviteRoles.js'
export * from './getPrefixForLocalStorage.js'
export * from './addSmblsSubdomains.js'
export * from './generateSitemap.js'
export * from './getPluginSessionFromUrl.js'
// Never registered here — workspace/functions/reminders.js:46 calls
// `el.call('getActiveWorkspacePointer')` and silently got `undefined`.
export * from './activeWorkspacePointer.js'
// Which orgs a list OFFERS: a team member's own EMPTY personal org
// (/auth/me `hasContent: false`) stays in the background (POB round 5, Nika
// 2026-10-31). The org switcher, the Studio link and the shell's /w hub.
export * from './visibleOrgs.js'
export * from './whitelabelScope.js'

export * from './appHeader.js'
export * from './appShell.js'
export * from './projects.js'
// THE APP SPLIT — projectKind / projectKindContext / splitProjectsByKind /
// loadProjectKindSignals: the ONE answer to "workspace app or website?"
// (docs/APP_SPLIT.md, WS-STUDIO-SPLIT-WORKSPACE-APPS-FROM-SITES).
export * from './projectKind.js'
// The SOLE copy of setNavbarTooltipPosition (duplicate-export dedup,
// 2026-07-27). It lives HERE, not in workspace/functions, because SHARED is
// the layer every app scope merges: preview's PreviewNavbar extends
// NavbarTooltipParent in four places, and preview/marketplace/gazer/demo/
// design-system merge shared WITHOUT workspace — so a copy that lived only in
// workspace would leave `el.call('setNavbarTooltipPosition')` unresolved there.
// The implementation kept is the one workspace used to shadow this file with;
// the version previously here had a `visible: false` hide-path bug (false is
// falsy, so `options.visible || 'default'` could never express "hide") and
// called a never-registered `exec` helper.
export * from './setNavbarTooltipPosition.js'
// The SOLE copy of markNotificationRead / markAllNotificationsRead
// (duplicate-name collision fix, 2026-07-27 — paired with the
// NotificationPanel move into ./components/Notifications.js). Same
// reasoning as setNavbarTooltipPosition above: canvas links shared but
// never workspace, so el.call('markNotificationRead', …) /
// el.call('markAllNotificationsRead') from the now-shared NotificationPanel
// must resolve here, not in workspace/functions.
export * from './markNotificationRead.js'
// createNotification / createNotifications (notifications/createNotification.js)
// were never registered here — the function's own JSDoc promises
// "Callable via el.call('createNotification', { ... })", but only two
// consumers (chat's subscribeRealtime.js mention-notify, calendar's
// saveEvent.js invite-notify) ever reached it, both via a direct static
// import, so the gap went unnoticed. `el.call` on an unregistered name
// silently resolves to `undefined` (no throw, no network call — see the
// records-plane "unregistered-fn silent empties" class of bug) instead of
// failing loudly, which is how a workspace-shell caller (PersonPopover's
// Ping action) discovered this: the "Ping sent" toast fired even though no
// notification was ever created. Exporting it here fulfills the function's
// own documented contract and costs nothing — it's a pure addition, the two
// existing static-import consumers are unaffected.
export * from './notifications/createNotification.js'
// listNotifications — the canonical fetch behind NotificationWrapper
// (components/Notifications.js). Local, self-contained (own ./sdk.js
// getSDK, no cross-package import) — see that file's own header for why.
export * from './notifications/listNotifications.js'
export * from './formatImports.js'
export * from './inviteMember.js'
export * from './authBridge.js'
export * from './canvasUrl.js'
export * from './sessionBridgePeers.js'
// the org switcher's "Try again" (components/AppOrgSwitcher.js THE SWITCH DEADLINE)
export { retryOrgSwitch } from './retryOrgSwitch.js'
// What a /_session-bridge page hands its peer: the access token only.
export * from './sessionBridgeTokens.js'
// the /_session-bridge answer — the shell page and the light bridge entry
export * from './sessionBridgeResponder.js'
// The peer side: which bridge to frame, the one forced re-ask, and the
// renewal for an SDK without the session-reacquirer hook. In the bag so the
// imports in sdkClient.js / bootResolve.js resolve on a published surface.
export * from './sessionBridgePeerOrigin.js'
// The comm host (comm.symbols.app): its profile list and its host pairing —
// read by the session bridge, the nav filter and the shell's route guard.
export * from './commProfileData.js'
export * from './commHost.js'
// where the navbar sits — top, a left / right rail, or the bottom (AppHeader)
export * from './navbarLayout.js'
// a page's own sidebar: its side (opposite a navbar rail), the person's
// per-page hidden choice and the one toggle (AppSidebar, the PageSlot icon)
export * from './pageSidebar.js'
export * from './sessionBridgeReacquire.js'
export * from './sessionBridgeRenewal.js'
// Who owns this origin's session: markOwnSession() after every sign-in
// (review D1; sessionOwnership.js).
export * from './sessionOwnership.js'
// Re-enabled 2026-08-09 (frank-compile pass) — the "zero live callers here"
// claim was stale: `compileCode.js`'s `method === 'markdown-to-symbols'`
// branch reads `this.context.functions.MarkdownToDomQLCompiler` (populated
// from THIS barrel), so with the export missing the compile mode threw
// `TypeError: … is not a constructor` on every call, silently caught by
// compileCode's own try/catch and rendered as a "Compilation Error" to the
// user. Was also a `frank to-json` dropped-export finding.
export * from './MarkdownToDomQLCompiler.js'
export * from './compileCode.js'

// more
export * from './htmlfy.js'
export * from './codify.js'
// Re-enabled 2026-08-09 (frank-compile pass) — `@symbo.ls/router` never
// exported `getLocationPath` (only `getActiveRoute`); route.js's own
// (broken) import was fixed by inlining the local last-path-segment helper
// (`_lastRouteSegment`, same shape as `component.js`'s existing
// `getLastLocationPath` inline fix). Was 14 `frank to-json` dropped-export
// findings — every export here threw at call time and none of these
// route-active checks worked.
export * from './route.js'
export * from './themes.js'
export * from './color.js'
export * from './modal.js'
export * from './appDialogClose.js'
export * from './appDialogSync.js'
export * from './appDialogFocusPanel.js'
export * from './appDialogTrapTab.js'
export * from './appDialogCloseNearest.js'
// The kit pager's window of pages (AppPagination).
export * from './appPageWindow.js'
export * from './crypto.js'
export * from './arrayze-icons.js'
// ntc.js (nameTheColor, ~42 KB color table) is intentionally NOT in the eager
// barrel — its only consumer (add-color-modal) dynamic-imports it at use time
// (bundle-split T2). Left as a documented `frank to-json` dropped-export
// finding (tickets/fable.md, 2026-08-09 frank-compile pass) — adding it here
// would make Parcel fold the 42 KB table into the eager chunk, defeating the
// split; frank's dropped-export check has no notion of "reachable only via
// dynamic import" as a valid non-barrel path.
// export * from './ntc.js'
//
// Re-enabled 2026-08-09 (frank-compile pass) — the "requires @symbo.ls/router"
// exclusion was stale: component.js dropped that import long ago (see
// `goToRemovedComponentEditorRoute`'s own comment inlining the local
// last-path-segment helper instead). Its only remaining import,
// `deepClone` from `@symbo.ls/utils`, is a normal installed dependency. Was
// 9 `frank to-json` dropped-export findings, and canvas's
// `CanvasContextMenu.js` calls `el.call('toggleComponentFavorite'|
// 'toggleComponentLock')` — both silently no-op'd with the export missing.
export * from './component.js'
export * from './functions.js'
export * from './files.js'
export * from './collections.js'
export * from './icons.js'
export * from './fetch.js'
export * from './packages.js'
export * from './screenshot.js'
export * from './studio.js'
export * from './overlay.js'
export * from './intersectionObserver.js'
export * from './canvas.js'
export * from './frameListener.js'
export * from './grid.js'
export * from './versions.js'
export * from './fullscreen.js'
export * from './chosen.js'
export * from './copy-paste.js'
export * from './keyshortcuts.js'
export * from './auth.js'
export * from './privilegies.js'
export * from './svg.js'
export * from './prompt.js'
export * from './loader.js'
// `./client-context` was removed 2026-07-27 — same `createEmotion` /
// `transformDOMQLEmotion` v3-era imports as `methods/renderer-component.js`
// (see the note in `methods/index.js`). Its single export,
// `createClientContext`, had zero callers anywhere in the checkout.
export * from './spacing.js'
export * from './floating-panel.js'
export * from './design-system.js'
export * from './tooltip.js'
export * from './url.js'
export * from './capitalize.js'
export * from './rename.js'
export * from './dependencies.js'
export * from './typography.js'
export * from './shadows.js'
export * from './state.js'
export * from './sequence-selectors.js'
export * from './text-styles.js'
export * from './keyboard-shortcuts.js'
// Re-enabled 2026-08-09 (frank-compile pass) — the broken-import exclusion
// was stale: the file's own header comment already documents that
// `autocompletePropKey`/`prefillValue` (the only two consumers of the
// missing `@symbo.ls/ui` import) were removed outright rather than left as
// build-breaking stubs; the three exports that remain
// (`openAutocompleteDropdown`/`closeAutocompleteDropdown`/`moveThroughList`)
// never touched that import and have no remaining dependency on it. Was 3
// `frank to-json` dropped-export findings. The six sibling prefill*
// functions that don't need that import live in prefill-values.js.
export * from './prefill-layers.js'
export * from './prefill-values.js'
export * from './is-visible.js'
export * from './media-query.js'
export * from './MEDIATYPES.js'
export * from './canvas-navigation.js'
export * from './customColorPicker.js'
export * from './properties-dropdown.js'
export * from './policyCard.js'

export * from './presence.js'
export * from './liveCursors.js'
// Moved from workspace/functions 2026-08-09 (tickets/fable.md cross-package
// import fix) — listOrgMembers/invalidateOrgMembers. Not el.call'd anywhere
// (every consumer imports the module directly); barreled anyway for
// consistency with the rest of this file.
export * from './listOrgMembers.js'
export * from './installable.js'

// Device-capability layer (touch / coarse-pointer) for iPad touch parity —
// el.call('isCoarsePointer') + <html data-pointer> + syncPointerCapability().
export * from './pointerCapability.js'
// Long-press → synthetic contextmenu shim (coarse-pointer) — el.call('armLongPress').
export * from './longPress.js'
export * from './edgeSwipe.js'
// Phone header scroll-condense (one document-level arm) —
// el.call('armNavCondense', <phone-width media query>).
export * from './navCondense.js'
// Native device-shell detection (Tauri WKWebView) — el.call('isNativeShell').
export * from './isNativeShell.js'

// Persona (tickets/fable.md PERSONA-6 + opus.md PERSONA-1/2/3 + sonnet.md
// PERSONA-4/5) — role catalog, disabled-attribution helper, and defensive
// SDK wrappers for the "view as <role>" affordance (components/PersonaPill.js).
// Everything here degrades to a safe no-op until PERSONA-1/4/5 land.
export * from './persona.js'
// personaSession.js — the sessionStorage session-blob owner (PERSONA-2).
// Barreled separately (not re-exported by persona.js) so PERSONA-5's
// cross-package consumers (workspace/functions/loadPrivateData.js) can reach
// `clearPersonaSession` via the standard `_wsShared.functions.X` deferred-call
// pattern instead of a relative cross-package import (FA204 — module-local
// helpers aren't frank-safe across a package boundary).
export * from './personaSession.js'

// Runner shell — Phase 3
// `_causeChain`/`_isTokenRejection` are underscore-prefixed pure helpers,
// exported ONLY for direct unit-test import
// (bootShell.authClassification.test.js) — tried barrelling them 2026-08-09
// (frank-compile pass) to close 2 dropped-export findings, but it made
// frank's globalScope lift reach further module-scope closures that came
// out MINIFIED (single-letter globalScope keys). Reverted; left as
// documented dropped-export findings (tickets/smbls.md) — this is the
// test-only-export convention already established elsewhere in this file
// (see ntc.js's nameTheColor note above), not a runtime bug: both helpers
// are already called bare inside bootShell.js itself.
export { bootShell } from './bootShell.js'
// The ONE boot sequence's three steps — run by bootShell (parcel, via the
// `steps` its caller passes) and by the published shell's boot plugin
// (packages/workspace/context.js, via context.functions).
export { bootPrepare } from './bootPrepare.js'
export { bootResolve } from './bootResolve.js'
export { bootApply } from './bootApply.js'
// buildSdkAdapter deliberately NOT barrelled alongside
// registerSdkAdapter — tried adding them 2026-08-09 (frank-compile pass):
// both have zero `el.call` consumers (only ever used via bootShell.js's own
// direct `import … from './sdkAdapter.js'`), and barrelling them made
// frank's globalScope multi-file-helper lift reach several NEW module-scope
// closures inside sdkAdapter.js/bootShell.js that hadn't needed lifting
// before, several of which came out MINIFIED (single-letter globalScope
// keys `O`/`R`/`S`/`B`/`g2`) — a distinct, worse defect class than the 2
// dropped-export findings it would have closed. Reverted; left as a
// documented `frank to-json` dropped-export finding (tickets/smbls.md).
export { registerSdkAdapter } from './sdkAdapter.js'
// Canonical language registry — the ONE place the platform's shipped
// languages, their switcher labels, their direction and the
// enforced > user > device > workspace > brand > English resolution order are
// declared. `enforcedWorkspaceLang` is the ONE reader of the
// `settings.language` + `settings.languagePolicy` pair: every surface that
// asks "is this workspace's language a rule or a default?" calls it rather
// than testing the policy string itself.
export {
  DEFAULT_LANG,
  DEFAULT_LANGUAGE_POLICY,
  LANGUAGE_POLICIES,
  PLATFORM_LANGUAGES,
  enforcedWorkspaceLang,
  isSupportedLang,
  langDir,
  resolveLang
} from './languages.js'
export { setLang } from './setLang.js'
// Re-resolve the session language for the workspace that is active NOW. The
// enforced level of the precedence belongs to a workspace, not to the person,
// so it is the one level that changes without a reload — see the file header.
export { applyWorkspaceLangPolicy } from './applyWorkspaceLangPolicy.js'
export { pageTitle, brandName, resolvePageMetadataTitle } from './pageTitle.js'
export {
  loadI18nNamespace,
  loadI18nNamespaces,
  hasI18nBundle,
  dropNonEnglishCache,
  getLoadedNamespaces,
  mergeAllNamespaces,
  // The BUNDLED translations — `context.polyglot.translations`. ENGLISH ONLY:
  // it is the fallback for every miss, and `translate()` returns the key
  // ITSELF when nothing resolves, so the base language must never need a round
  // trip. Every other language is a lazily-imported, content-hashed chunk per
  // namespace (`loadI18nNamespace`) — see i18nLoader.js's header for the
  // Parcel measurements behind that split.
  i18nStaticTranslations,
  i18nStaticEnglish
} from './i18nLoader.js'
export * from './switchers/index.js'

// House relative-time formatter — el.call('timeAgo', ts) anywhere.
export { timeAgo } from './timeAgo.js'

// Canonical IANA-timezone label formatter — el.call('zoneLabel', tz) anywhere
// (TZ-LABELS-1 — the sole implementation; see zoneLabel.js header).
export { zoneLabel } from './zoneLabel.js'

// Toast chime — el.call('playNotifySound') anywhere. Paired with
// openNotification's `sound: true` flag (methods/notification.js).
export { playNotifySound } from './notifySound.js'

// HTML -> one line of plain TEXT — el.call('htmlToPlainText', str). For every
// preview that prints a server string which may carry markup (a calendar
// invitation's description is HTML). See htmlToPlainText.js.
export { htmlToPlainText } from './htmlToPlainText.js'
// A date or a time in the SESSION language — el.call('localizedDate', value,
// options). Chrome ships no Georgian locale data, so the month and weekday
// names come from the bundle there. See localizedDate.js.
export { localizedDate } from './localizedDate.js'
// The ink for a plate whose HUE comes from data — el.call('inkOnPlate', token).
// Picks the brand plate ink with the higher contrast. See inkOnPlate.js.
export { inkOnPlate } from './inkOnPlate.js'

// Shared PageSlot-vs-navigate decision — el.call('openInSlot', href, el)
// opens `href` in the shell PageSlot when its route supports it, else falls
// back to a normal navigation. One canonical implementation for
// NotificationRow, GlobalNotification's toast click, and the global search
// dropdown (workspace navigateSearchResult) — see openInSlot.js header for
// the full resolution contract.
export { openInSlot, canOpenInSlot } from './openInSlot.js'

// Notification click dispatcher — el.call('openNotificationTarget', { href,
// type, metadata }, el) opens the notification's FEATURE MODAL in place when
// it references a ticket/event (per Nika 2026-08-06 "tickets, events and such
// features should be opened in their modals"), and falls back to the
// openInSlot decision above for everything else. Wired from NotificationRow
// (bell panel + /inbox) and the toast click (GlobalNotification.js).
// isCrossOrgNotificationHref/resolveNotificationTarget: tried barrelling
// alongside openNotificationTarget 2026-08-09 (frank-compile pass) to close
// 2 dropped-export findings; reverted for the same globalScope-lift-fanout
// reason as bootShell's helpers above. Test-only exports
// (notificationScope.test.js, openNotificationTarget.test.js), zero
// `el.call` consumers — left as documented dropped-export findings.
export { openNotificationTarget } from './openNotificationTarget.js'
// Where a notification opens when its row carries no href (the channel types:
// the sender's CRM contact, else the CRM inbox) — el.call('notificationHrefOf',
// { href, type, metadata }); NotificationRow and the toast share it.
export { notificationHrefOf } from './notificationHrefOf.js'

// Entity drag-and-drop wire format — el.call('setDragRef', e, ref) on a
// source, el.call('hasDragRef'/'hasDragFiles', e) during dragover,
// el.call('readDragRef', e) on drop. One format so any surface (tickets,
// projects, project pages, files) can be dropped into any target (chat
// composer, canvas prompt) without packages importing from each other.
// See dragRef.js for the wire contract and why it double-writes text/plain.
export {
  DRAG_REF_MIME,
  DRAG_REF_KINDS,
  REF_DROP_ATTR,
  setDragRef,
  hasDragRef,
  hasDragFiles,
  readDragRef,
  dragRefLabel,
  dragRefIcon,
  dragRefToText,
  refDropTargetFrom,
  dispatchRefDrop
} from './dragRef.js'

// EntityRef — THE one contract for naming a workspace entity (VaultOS v2
// design §2): `{ type, id, … }` and its string form `ref:<type>:<id>`. Reached
// as el.call('parseEntityRef' | 'formatEntityRef' | 'isEntityRef' |
// 'entityRefHref' | 'entityRefIcon', …) so the canvas entity card, the
// assistant's Sources chips, CommentThread and any module page all resolve one
// implementation. Drag-ref `kind` === EntityRef `type` (see DRAG_REF_KINDS
// right above). Named exports, not `export *`: a bag re-export is the shape
// that publishes as "" for externalized specifiers, and the explicit list is
// what the frank extract guard counts.
export {
  ENTITY_REF_TYPES,
  parseEntityRef,
  formatEntityRef,
  isEntityRef,
  entityRefHref,
  entityRefIcon,
  entityRefKindKey
} from './entityRef.js'

// CommentThread's pure half (VaultOS v2 §3.5) — the ordering/tombstone/
// permission reducer and the two request builders, kept out of the component
// so they are unit-testable without a browser or a server. Reached as
// el.call('commentThreadRows' | 'commentCreatePayload' | 'commentListFilter' |
// 'commentTimeLabel' | 'commentAuthorLabel' | 'commentRowsFrom' |
// 'canManageComment', …).
export {
  commentRowsFrom,
  commentAuthorLabel,
  commentTimeLabel,
  canManageComment,
  commentThreadRows,
  commentCreatePayload,
  commentListFilter
} from './commentThread.js'

// Prompt composer attachment serialisation — el.call('buildPromptPayload', el, state)
// returns { body, attachments, isEmpty }; el.call('clearedPromptState') is the
// matching post-send patch. One source so the canvas composer and the shell's
// own send override can't disagree about what a prompt body contains — they
// did, and the live one dropped every attachment. See promptAttachments.js.
export {
  buildPromptPayload,
  clearedPromptState,
  removePromptAttachment,
  removePickedElement
} from './promptAttachments.js'

// Account (avatar) menu — opens the shared ContextDropdown. openAccountPage is
// called BY NAME (the menu's Settings / Profile rows, the profile hero), so it
// must be registered here: an unregistered el.call name answers undefined.
export {
  accountMenuItems,
  accountMenuAction,
  openAccountMenu,
  openAccountPage
} from './accountMenu.js'
// Top-navbar tab right-click menu (pin / side view / remove) — see
// navbarTabMenu.js; the pure helpers ride along for direct ESM readers.
// toggleCustomNavbarPin is the search-result "Pin to navbar" action
// (workspace/functions/navSearchResultMenu.js) — same prefs bag, foreign
// (non-base-tab) paths.
// `_resolveWsKey` is an underscore-prefixed pure async helper, exported ONLY
// for direct unit-test import (`__tests__/navbarTabMenuSave.test.js`) — tried
// barrelling it 2026-08-09 (frank-compile pass) for the same
// globalScope-lift-fanout reason as bootShell's helpers above; reverted,
// left as a documented dropped-export finding.
export {
  navbarTabMenuItems,
  navbarTabMenuAction,
  navbarTabPrefs,
  applyNavbarTabPrefs,
  saveNavbarTabOrder,
  toggleCustomNavbarPin
} from './navbarTabMenu.js'

// Assistant markdown renderer — el.call('renderAssistantMarkdown', text) turns a
// finished assistant message into a DOMQL children tree via symbolize-markdown.
// Shared so docs (brender), preview and workspace render it identically; the
// el.call seam keeps it brender-safe. See _simone/ChatMessage.js Md.children.
export { renderAssistantMarkdown } from './renderAssistantMarkdown.js'

// Cross-app preference cookie (FA001 fix — VoiceOverlay.js used to import
// '../prefs.js' directly, a forbidden sibling import from a components file).
// The functions/ barrel is the registered seam project code reaches this
// through: el.call('getPref', key) / el.call('setPref', key, value).
//
// `ACTIVE_ORG` is a pref KEY CONSTANT, not a callable — `el.call` can only
// reach callables, so a consumer that needs the raw key (to build the
// `setPref`/`getPref` call itself) has to import it as a value. Re-exporting
// it here (instead of a bare `@symbo-ls/workspace-shared/prefs.js` deep
// import) puts it inside the `functions/` bag frank's `declaredLibResolution`
// already discovers for every OTHER shared-library import — a bare
// `prefs.js` subpath import has no discovered context bag, so it isn't
// context-foldable and frank correctly refuses to guess (`unresolvable-
// npm-module-sync-guard`, canvas/components/DashboardRows.js's
// `selectWorkspace`). See tickets/fable.md "CANVAS-FRANK-SYNC-16-AND-7".
export { getPref, setPref, clearPref, ACTIVE_ORG } from '../prefs.js'

// Cross-app session bridge (../crossAppAuth.js) — collectSdkTokens reads the
// live SDK token pair for the iframe/cookie handoff the standalone
// session-bridge page performs. Same "reaches outside functions/" shape as
// getPref above: the functions/ barrel is the only seam that survives frank
// serialization (a per-file deep specifier like
// '@symbo-ls/workspace-shared/crossAppAuth.js' cannot resolve through the
// public importmap once this package is pushed as a library — root-cause of
// tickets/catalog-fixall.md P0 "workspace-shell catalog page dead"; fixed at
// EVERY consumer site by importing the package root and reading
// `sharedDefault.functions.<name>` instead of a subpath).
export { collectSdkTokens } from '../crossAppAuth.js'

// Org-integration capability layer (./integrations/index.js — relocated
// 2026-08-15 from ../integrations/ into this discovered functions/ folder;
// frank only ever discovers components/, snippets/, pages/, functions/,
// methods/, designSystem/, files/, assets/, so a top-level integrations/
// folder was silently dropped at publish, and any deep-subpath import of it
// (`@symbo-ls/workspace-shared/integrations/index.js`) fell through the
// public importmap and served a black page — see tickets LANE M) —
// externalData.js (the /data/external backend-visualization page) calls
// these to list connector kinds and dispatch instantdb capability calls.
// Also the admin Integrations hub + per-kind detail pages
// (pages/admin/integrations*.js) — loadIntegrationCatalogue/saveIntegration/
// deleteIntegration were added alongside those consumers' deep-subpath fix
// (tickets/execution-board.md §1). Same deep-subpath-can't-survive-push
// reasoning as collectSdkTokens above.
export {
  callCapability,
  listIntegrationKinds,
  listScopedIntegrations,
  loadIntegrationCatalogue,
  saveIntegration,
  deleteIntegration,
  // The rest of this module's public surface — missing here the same way
  // installMarketplaceIntegration/uninstallMarketplaceIntegration were
  // (WS-SHARED-BARREL-MISSES-MARKETPLACE-INTEGRATION-INSTALL-EXPORTS-1): an
  // explicit named list, not `export *`, so a name absent from the list is
  // silently `undefined` through the flat `functions` bag a consumer's
  // context carries instead of throwing. barrelImports.test.js now locks
  // this list to integrations/index.js's full public surface.
  listOrgIntegrations,
  addIntegrationFromMarketplace,
  assignIntegrationScope,
  reorderIntegrations,
  loadIntegrationUnits,
  loadOrgIntegrations,
  loadWorkspaceIntegrations,
  loadProjectIntegrations,
  findIntegrationsByCapability,
  listMarketplaceEntitlements,
  checkMarketplaceEntitlement,
  installMarketplaceIntegration,
  uninstallMarketplaceIntegration
} from './integrations/index.js'

// Extension iframe embedding (./iframeApp/index.js — relocated 2026-08-15,
// same reasoning as integrations/ above) — createIframeApp mounts a
// sandboxed extension iframe; injectSprites forwards the host's icon sprite
// sheet into it. Same deep-subpath-can't-survive-push reasoning as above.
export { createIframeApp, injectSprites } from './iframeApp/index.js'
// embedComponent / embedDesignSystem: canvas (CanvasItem, ComponentThumbnail,
// quickEditor) and preview (PreviewSidebar) import them from the deep
// `functions/iframeApp/index.js` path. Only barrel names reach this library's
// emitted functions bag, so unbarrelled they were undefined on every
// published consumer (__tests__/crossPackageDeepImportsBarrelled.test.js).
export { embedComponent, embedDesignSystem } from './iframeApp/index.js'
// USER-CODE-ORIGIN (canvas H1): the opaque user-code frame. `mountUserCode`
// mounts a project's own code with no session reach (preview renderProject);
// `userCodeSandbox()` is the frame's one sandbox token list (canvas CvFrame /
// ComponentThumbnail attr, preview iframes).
// getUserCodeHandle(node): the live user-code handle mounted in a frame
// (theme / dir / navigate messages, editor tools) — the registry stays private.
export {
  mountUserCode,
  prewarmUserCode,
  getUserCodeHandle,
  userCodeSandbox
} from './iframeApp/index.js'
// findScopeKey(args): the first scope-naming key (workspaceId, projectId, …)
// at any depth — a user-code frame never names where its data call lands.
export { findScopeKey } from './iframeApp/index.js'

// MCP connector admin (./mcp/index.js — relocated 2026-08-15, same reasoning
// as integrations/ above) — pages/admin/integrations-mcp.js lists/enables/
// disables/deletes workspace MCP connectors and triggers capability
// re-discovery. Same deep-subpath-can't-survive-push reasoning as
// collectSdkTokens above; this file is also reached from WITHIN shared's own
// pages barrel (pages/identity-index.js → this exact page), so its consumer
// uses the deferred-call wrapper, never a module-scope destructure.
export {
  listConnectors,
  installConnector,
  enableConnector,
  disableConnector,
  deleteConnector,
  discoverCapabilities
} from './mcp/index.js'

// Marketplace seed catalog (./marketplaceCatalog.js — relocated 2026-08-16 from
// ../data/ into this discovered functions/ folder; `data/` is not a frank
// DIRECTORY_KEY, so a declared-lib import of workspace-shared/data/* was
// externalised at publish and DROPPED — the marketplace catalog vanished from a
// published bundle, same class as integrations/ above). A NAMED export flattens
// into the functions bag so frank serialises it context-first; the
// marketplace/data/catalog.js shim bridges it back to a default for its
// downstream default-importers.
export { marketplaceCatalog } from './marketplaceCatalog.js'
// catalogSearchText: marketplace/functions/index.js imports it from the deep
// `functions/marketplaceCatalog.js` path — same reason as the iframeApp pair.
export { catalogSearchText } from './marketplaceCatalog.js'

// THE catalog search rule (./catalogSearch.js — word / prefix / light-stem
// match, exact > prefix > stem; the rule the assistant's store search runs on
// the server). Two consumers match through it: the store's filter, counts and
// rank (packages/marketplace) and the shell's App Store / integration
// suggestions (workspace functions/search/searchAppResults.js), which reaches a
// shared function only through this bag (`_wsShared.functions.X`).
export {
  catalogSearchRank,
  catalogSearchIncludes,
  catalogSearchThreshold
} from './catalogSearch.js'

// The profile surface's resolver + loader (./userProfileData.js). The shell's
// onWorkspaceChange (workspace/functions/loadPrivateData.js) imports both from
// the deep `functions/userProfileData.js` path to re-run on a workspace switch
// what the page runs on mount (PROFILE-PAGE-NO-REHYDRATE-ON-SWITCH-1). Only
// barrel names reach this library's emitted functions bag: unbarrelled, the
// published shell read `isUserProfilePath` as undefined (measured 2026-10-07
// on the served dev shell) — __tests__/crossPackageDeepImportsBarrelled.test.js.
export { hydrateUserProfile, isUserProfilePath } from './userProfileData.js'

// Voice input session — mic + speech engine + spoken replies behind the shared
// VoiceOverlay (see architecture/VOICE_COMMANDS.md). Mic buttons call
// voiceToggle / voicePttPress / voicePttRelease; the overlay wires
// voiceOnStateChange + voiceTeardown from its lifecycle.
export {
  voiceStart,
  voiceStop,
  voiceToggle,
  voiceSetMode,
  voiceExpand,
  voiceMinimize,
  voiceFocusComposer,
  voiceCallFrame,
  voicePttPress,
  voicePttRelease,
  voicePttCancel,
  voiceCommitAndClose,
  voiceOnStateChange,
  voiceTeardown,
  voiceStopSpeaking,
  voiceEngineFallback,
  voiceMicToggle,
  voiceAddTurn,
  voiceRetry,
  appendVoiceTurn
} from './voiceSession.js'
// The ElevenLabs conversational-agent engine (voiceConvai.js) is removed
// (2026-10-07): Talk runs the SIMONE loop on the server pipeline only.
export * from './_safeMap.js'
export * from './activeWorkspaceRecord.js'
// The name the assistant's empty state addresses ("Ask anything about
// <workspace>") — one resolver for the shared Chat hint and the workspace
// starter builder.
export * from './assistantScopeName.js'
export * from './aiService.js'
export * from './aiModelCatalog.js'
export * from './aiProviderDiagnostics.js'
export * from './appDependencyGraph.js'
// The answers of an ask_user question card — the ONE rule the thread's
// ChatCard and AiAsk (the island, the full-screen call) read.
export * from './askCardOptions.js'
export * from './attachDbAdapters.js'
export * from './authCallbackError.js'
export * from './authGuard.js'
export * from './authHandoff.js'
export * from './handoffTargets.js'
export * from './blob-node.js'
export * from './bootRedirectQuery.js'
export * from './browserWebSocket.js'
export * from './canvasBackground.js'
export * from './clearAuthStorage.js'
export * from './clipboard.js'
export * from './closeModalByStateKey.js'
export * from './coreHeaders.js'
export * from './dateUtils.js'
export * from './diagnoseAuth.js'
export * from './featureFlags.js'
// ORG_SWITCHER_DROPDOWN_ID: the workspace shell's org-switcher Panel pin
// imports it — barrelled for the same deep-subpath-can't-survive-push reason.
export * from './orgSwitcherDropdown.js'
export * from './fetchProject.js'
// loadPublicPreviewData: fetchProject's preview-host branch (public only).
export * from './publicPreviewData.js'
export * from './guestRoutes.js'
export * from './idleState.js'
export * from './inviteToken.js'
export * from './whitelabelAuthPaint.js'
export * from './isLoudDevRuntime.js'
export * from './keyboardAvoidance.js'
export * from './layersProperties.js'
export * from './marketplaceFilters.js'
export * from './mergeProjectIntoEditorContext.js'
export * from './minimizedSlots.js'
export * from './moduleRoutes.js'
export * from './mwtFlag.js'
export * from './mwtNav.js'
export * from './newProjectPicker.js'
export * from './notificationScope.js'
export * from './peopleUtils.js'
export * from './postOAuthNext.js'
export * from './signupIntent.js'
// NOT './prefill-layers.js' — it is excluded ~180 lines above, on purpose:
// it imports `@symbo.ls/ui/Autocomplete/props`, and that package is a broken
// symlink in this checkout (its target was never checked out). Re-adding it
// here — presumably by an alphabetical sweep that did not read the earlier
// block — fails the WHOLE workspace bundle at resolve time, so every dev
// server serves a Parcel error page instead of the app. The functions that
// do not need that import already come from './prefill-values.js' above.
// (owner, key) refs for the shell's by-key reads, and the candidates of a
// 409 `ambiguous_key` (insert panel library, switchProjectByKey).
export * from './projectKeyRefs.js'
export * from './projectUrls.js'
export * from './projectsRegistry.generated.js'
export * from './resolveProjectParams.js'
export * from './safeCall.js'
export * from './sdk.js'
// Teams directory (Mongo-backed org/team membership CRUD) — moved here so
// MemberProfile's Team row works from every host. See teams.js header.
export * from './teams.js'
export * from './speakReply.js'
// THE workspace voice (settings.voice.voiceId) — one reader for the voice
// overlay and speakReply.
export * from './workspaceVoice.js'
export * from './sdkRealtimeProvider.js'
export * from './signout.js'
export * from './slotPane.js'
export * from './splitProjectForEditor.js'
export * from './supabaseFetchAdapter.js'
export * from './systemStatus.js'
export * from './themeMode.js'
export * from './themeResolved.js'
export * from './userFacingError.js'
export * from './workspaceApiBase.js'
export * from './collectionGrantsOf.js'
export * from './workspaceModuleRef.js'
export * from './workspacePrefix.js'
export * from './assistantModePlaceholder.js'
export * from './workspaceUrlSync.js'
// White-label persona sign-in (used by the branded sign-in surface).
export * from './bootTrail.js'
export * from './resolveAuthNext.js'
export * from './whitelabelOnboarding.js'
export * from './whitelabelTurnstile.js'
export * from './whitelabelSignin.js'
// THE shell overlay reader (ModalRoot stack / DropdownRoot layer) — one source
// for the workspace guard, the home drawer and the marketplace (standalone too).
export * from './shellOverlayOpen.js'
// the header search's close writes — an outside press and the search button's
// second press (AppHeader › HeaderSearch, HeaderTabs › SearchBtn)
export * from './headerSearchClose.js'
export { applyPublicBootLang, settleWhitelabelBrand } from './publicBootLang.js'
// the glyph resolver + the app you are in (dropdowns spec 2026-09-30 §3.3, §5, §12)
export * from './glyphNames.js'
